import React, { useState, useEffect, useRef, useCallback } from 'react';

const TOTAL = 5;
const DURS = [4500, 4000, 4500, 5000, 5500];

export default function MotionDemo() {
  const [cur, setCur] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const elapsed = useRef(0);
  const lastTs = useRef(null);
  const rafId = useRef(null);
  const curRef = useRef(0);

  const goTo = useCallback((n) => {
    const next = ((n % TOTAL) + TOTAL) % TOTAL;
    curRef.current = next;
    setCur(next);
    elapsed.current = 0;
    lastTs.current = null;
    setProgress(0);
  }, []);

  const tick = useCallback((ts) => {
    if (lastTs.current === null) lastTs.current = ts;
    elapsed.current += ts - lastTs.current;
    lastTs.current = ts;
    const pct = Math.min((elapsed.current / DURS[curRef.current]) * 100, 100);
    setProgress(pct);
    if (elapsed.current >= DURS[curRef.current]) goTo(curRef.current + 1);
    rafId.current = requestAnimationFrame(tick);
  }, [goTo]);

  useEffect(() => {
    if (playing) {
      rafId.current = requestAnimationFrame(tick);
    } else {
      cancelAnimationFrame(rafId.current);
      lastTs.current = null;
    }
    return () => cancelAnimationFrame(rafId.current);
  }, [playing, tick]);

  const prev = () => { cancelAnimationFrame(rafId.current); goTo(cur - 1); if (playing) { elapsed.current = 0; lastTs.current = null; rafId.current = requestAnimationFrame(tick); } };
  const next = () => { cancelAnimationFrame(rafId.current); goTo(cur + 1); if (playing) { elapsed.current = 0; lastTs.current = null; rafId.current = requestAnimationFrame(tick); } };

  const css = `
    .mdw{background:#0d1117;border-radius:12px;width:100%;aspect-ratio:16/9;position:relative;overflow:hidden;font-family:Inter,-apple-system,sans-serif;}
    .mds{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:6%;opacity:0;transition:opacity 0.5s ease;gap:clamp(6px,1.2vw,16px);}
    .mds.on{opacity:1;}
    @keyframes mdFU{from{opacity:0;transform:translateY(16px);}to{opacity:1;transform:translateY(0);}}
    @keyframes mdSI{from{opacity:0;transform:scale(0.4);}to{opacity:1;transform:scale(1);}}
    @keyframes mdSG{from{width:0;}to{width:100%;}}
    @keyframes mdSR{from{opacity:0;transform:translateX(-24px);}to{opacity:1;transform:translateX(0);}}
    @keyframes mdSL{from{opacity:0;transform:translateX(24px);}to{opacity:1;transform:translateX(0);}}
    @keyframes mdGI{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}
    @keyframes mdFB{0%{box-shadow:none;}40%{box-shadow:0 0 80px 20px rgba(37,99,235,0.35);}100%{box-shadow:none;}}
    @keyframes mdWF{from{opacity:0;transform:translateY(5px);}to{opacity:1;transform:translateY(0);}}
    .mds.on .a1{animation:mdFU .5s .1s ease both;}
    .mds.on .a2{animation:mdFU .5s .5s ease both;}
    .mds.on .a3{animation:mdFU .5s .9s ease both;}
    .mds.on .a4{animation:mdFU .5s 1.3s ease both;}
    .mds.on .aR{animation:mdSR .5s .2s ease both;}
    .mds.on .aL{animation:mdSL .5s .6s ease both;}
    .mds.on .aG{animation:mdGI .6s 1.4s ease both;}
    .mds.on .stag{animation:mdFU .4s .1s ease both;opacity:0;}
    .mds.on .sm1{animation:mdFU .5s .4s ease both;opacity:0;}
    .mds.on .sm2{animation:mdFU .5s .9s ease both;opacity:0;}
    .mds.on .ss{animation:mdFU .5s 1.4s ease both;opacity:0;}
    .mds.on .avbef{animation:mdSR .5s .1s ease both;opacity:0;}
    .mds.on .avaft{animation:mdSL .5s .5s ease both;opacity:0;}
    .mds.on .av-final{animation:mdGI .6s 1.4s ease both;opacity:0;}
    .mds.on .wd1{animation:mdWF .3s .1s ease both;opacity:0;}
    .mds.on .wd1::after{animation:mdSG .5s .4s ease both;}
    .mds.on .wd2{animation:mdWF .3s .5s ease both;opacity:0;}
    .mds.on .wd2::after{animation:mdSG .5s .85s ease both;}
    .mds.on .wd3{animation:mdWF .3s .9s ease both;opacity:0;}
    .mds.on .wd3::after{animation:mdSG .5s 1.25s ease both;}
    .mds.on .wd4{animation:mdWF .3s 1.3s ease both;opacity:0;}
    .mds.on .wd4::after{animation:mdSG .5s 1.65s ease both;}
    .mds.on .fini-big{animation:mdSI .3s 2s ease both,mdFB .6s 2s ease both;opacity:0;}
    .mds.on .fini-sub{animation:mdFU .4s 2.5s ease both;opacity:0;}

    .hw{display:flex;flex-direction:column;align-items:center;gap:clamp(8px,1.5vw,18px);}
    .hl{display:flex;align-items:center;gap:clamp(10px,1.8vw,22px);}
    .ht{font-size:clamp(12px,2.2vw,26px);font-weight:900;min-width:clamp(36px,4vw,52px);}
    .hx{font-size:clamp(14px,3.2vw,42px);font-weight:800;}
    .hd{width:2px;height:clamp(14px,2.5vw,28px);background:#1e293b;margin:0 auto;}
    .hs{font-size:clamp(9px,1.2vw,14px);color:#475569;font-style:italic;}

    .pw{display:flex;flex-direction:column;align-items:center;gap:clamp(6px,1.2vw,14px);}
    .pc{font-size:clamp(36px,11vw,130px);font-weight:900;line-height:1;letter-spacing:-2px;color:#475569;}
    .psub{font-size:.38em;letter-spacing:0;}
    .pp{font-size:clamp(12px,2.4vw,28px);font-weight:700;text-align:center;line-height:1.3;color:#cbd5e1;}
    .pe{font-size:clamp(10px,1.5vw,18px);font-style:italic;color:#64748b;}

    .fini-scene{display:flex;flex-direction:column;align-items:center;gap:clamp(6px,1.2vw,14px);}
    .words-row{display:flex;gap:clamp(4px,1vw,12px);flex-wrap:wrap;justify-content:center;max-width:96%;}
    .wd{position:relative;font-size:clamp(10px,2vw,28px);font-weight:800;color:#e2e8f0;padding:clamp(4px,0.8vw,12px) clamp(8px,1.5vw,24px);background:#1a2035;border-radius:8px;border:1px solid #2d3748;overflow:hidden;}
    .wd::after{content:'';position:absolute;left:0;top:50%;height:2.5px;background:#3b82f6;}
    .fini-big{font-size:clamp(40px,13vw,160px);font-weight:900;color:#f1f5f9;line-height:.95;letter-spacing:-3px;text-align:center;}
    .fini-sub{font-size:clamp(9px,1.3vw,16px);color:#64748b;text-align:center;}

    .sw{display:flex;flex-direction:column;align-items:center;gap:clamp(6px,1.2vw,14px);}
    .stag{font-size:clamp(8px,1vw,12px);letter-spacing:2px;color:#3b82f6;text-transform:uppercase;background:#0f2444;padding:3px 12px;border-radius:20px;border:1px solid #1e3a5f;}
    .sm1{font-size:clamp(14px,3.5vw,44px);font-weight:900;color:#94a3b8;text-align:center;line-height:1.15;}
    .sm2{font-size:clamp(14px,3.5vw,44px);font-weight:900;color:#3b82f6;text-align:center;line-height:1.15;}
    .ss{font-size:clamp(10px,1.4vw,17px);color:#475569;font-style:italic;}

    .avcols{display:flex;align-items:stretch;}
    .avc{padding:clamp(8px,1.5vw,20px) clamp(10px,2vw,26px);}
    .avbef{background:#0a0d14;border-radius:10px 0 0 10px;border:1px solid #1a2030;}
    .avaft{background:#0f1929;border-radius:0 10px 10px 0;border:1px solid #2563eb;border-left:none;}
    .avh{font-size:clamp(8px,1.1vw,12px);font-weight:700;letter-spacing:2px;text-transform:uppercase;margin-bottom:clamp(6px,1.2vw,14px);}
    .avr{display:flex;gap:clamp(6px,1vw,12px);align-items:baseline;padding:clamp(3px,0.6vw,7px) 0;}
    .avt{font-size:clamp(10px,1.6vw,18px);font-weight:800;min-width:clamp(28px,3.5vw,46px);}
    .avd{font-size:clamp(10px,1.4vw,16px);line-height:1.2;}
    .av-final{font-size:clamp(11px,1.6vw,19px);color:#60a5fa;font-style:italic;font-weight:700;text-align:center;margin-top:clamp(6px,1.2vw,14px);}

    .mdprog{position:absolute;bottom:0;left:0;height:3px;background:#2563eb;transition:width 0.1s linear;}
    .mddots{position:absolute;bottom:10px;left:50%;transform:translateX(-50%);display:flex;gap:5px;}
    .mddot{width:5px;height:5px;border-radius:50%;background:#21262d;cursor:pointer;transition:background .3s;}
    .mddot.on{background:#2563eb;}
    .mdctrl{position:absolute;bottom:7px;right:10px;display:flex;gap:4px;opacity:0.3;transition:opacity .3s;}
    .mdw:hover .mdctrl{opacity:1;}
    .mdcb{background:rgba(13,17,23,.8);border:1px solid #30363d;color:#64748b;border-radius:5px;width:26px;height:26px;font-size:12px;cursor:pointer;display:flex;align-items:center;justify-content:center;}
    .mdcb:hover{color:#f1f5f9;}
  `;

  return (
    <>
      <style>{css}</style>
      <div className="mdw">

        <div className={`mds${cur === 0 ? ' on' : ''}`}>
          <div className="hw">
            <div className="hl a1"><span className="ht" style={{color:'#3b82f6'}}>18h</span><span className="hx" style={{color:'#f1f5f9'}}>Tu ranges tes outils.</span></div>
            <div className="hd a2" />
            <div className="hl a3"><span className="ht" style={{color:'#475569'}}>21h</span><span className="hx" style={{color:'#6b7280'}}>Tu rouvres ton ordi.</span></div>
            <div className="hs a4">Trop souvent, pour beaucoup d&#x27;artisans.</div>
          </div>
        </div>

        <div className={`mds${cur === 1 ? ' on' : ''}`}>
          <div className="pw">
            <div className="pc a1">21h<span className="psub">00</span></div>
            <div className="pp a2"><span style={{color:'#f1f5f9',fontWeight:900}}>La deuxi&#232;me journ&#233;e</span> commence.</div>
            <div className="pe a3">Devis &#224; refaire. Soir&#233;e qui file.</div>
          </div>
        </div>

        <div className={`mds${cur === 2 ? ' on' : ''}`}>
          <div className="fini-scene">
            <div className="words-row">
              <span className="wd wd1">Excel</span>
              <span className="wd wd2">Devis &#224; refaire</span>
              <span className="wd wd3">Facture &#224; envoyer</span>
              <span className="wd wd4">Soir&#233;e vol&#233;e</span>
            </div>
            <div className="fini-big">FINI<span style={{color:'#3b82f6'}}>.</span></div>
            <div className="fini-sub">Les devis &#224; 21h. La paperasse qui vole vos soir&#233;es.</div>
          </div>
        </div>

        <div className={`mds${cur === 3 ? ' on' : ''}`}>
          <div className="sw">
            <div className="stag">Avec DevisFast</div>
            <div className="sm1">Ce qui prenait la soir&#233;e</div>
            <div className="sm2">se r&#232;gle avant de rentrer.</div>
            <div className="ss">Le soir, vous soufflez vraiment.</div>
          </div>
        </div>

        <div className={`mds${cur === 4 ? ' on' : ''}`}>
          <div className="avcols">
            <div className="avc avbef aR">
              <div className="avh" style={{color:'#475569'}}>Avant</div>
              <div className="avr"><span className="avt" style={{color:'#475569'}}>18h</span><span className="avd" style={{color:'#4b5563'}}>Chantier fini</span></div>
              <div className="avr"><span className="avt" style={{color:'#475569'}}>21h</span><span className="avd" style={{color:'#4b5563'}}>Excel ouvert</span></div>
              <div className="avr"><span className="avt" style={{color:'#374151'}}>23h</span><span className="avd" style={{color:'#374151'}}>Encore dessus</span></div>
            </div>
            <div className="avc avaft aL">
              <div className="avh" style={{color:'#3b82f6'}}>Avec DevisFast</div>
              <div className="avr"><span className="avt" style={{color:'#60a5fa'}}>18h</span><span className="avd" style={{color:'#94a3b8'}}>Chantier fini</span></div>
              <div className="avr"><span className="avt" style={{color:'#60a5fa'}}>18h05</span><span className="avd" style={{color:'#f1f5f9',fontWeight:700}}>Devis envoy&#233;. &#10003;</span></div>
              <div className="avr"><span className="avt" style={{color:'#60a5fa'}}>21h</span><span className="avd" style={{color:'#f1f5f9',fontWeight:700}}>T&#233;l&#233;phone pos&#233;.</span></div>
            </div>
          </div>
          <div className="av-final aG">La soir&#233;e peut commencer.</div>
        </div>

        <div className="mdprog" style={{width:`${progress}%`}} />
        <div className="mddots">
          {[...Array(TOTAL)].map((_, i) => (
            <div key={i} onClick={() => goTo(i)} className={`mddot${i === cur ? ' on' : ''}`} />
          ))}
        </div>
        <div className="mdctrl">
          <button className="mdcb" onClick={prev}>&#8249;</button>
          <button className="mdcb" onClick={() => setPlaying(p => !p)}>{playing ? '\u23F8' : '\u25B6'}</button>
          <button className="mdcb" onClick={next}>&#8250;</button>
        </div>
      </div>
    </>
  );
}
